"use client";

import { useEffect, useState } from "react";
import { renderSlots } from "@/components/tools/template-slots";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

type SetKey = "lowercase" | "uppercase" | "digits" | "symbols";

const CHAR_SETS: Record<SetKey, string> = {
  lowercase: "abcdefghijklmnopqrstuvwxyz",
  uppercase: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  digits: "0123456789",
  symbols: "!@#$%^&*()-_=+[]{};:,.?",
};

const SET_KEYS: SetKey[] = ["lowercase", "uppercase", "digits", "symbols"];

const AMBIGUOUS_CHARS = new Set(["0", "O", "1", "l", "I"]);

const MAX_UINT32 = 0x1_00_00_00_00;

function randomInt(maxExclusive: number): number {
  // Rejection sampling to avoid modulo bias.
  const limit = MAX_UINT32 - (MAX_UINT32 % maxExclusive);
  const buffer = new Uint32Array(1);
  let value = MAX_UINT32;
  while (value >= limit) {
    crypto.getRandomValues(buffer);
    value = buffer[0];
  }
  return value % maxExclusive;
}

function filterAmbiguous(chars: string): string {
  return chars
    .split("")
    .filter((c) => !AMBIGUOUS_CHARS.has(c))
    .join("");
}

function activeCharSets(
  selected: Record<SetKey, boolean>,
  excludeAmbiguous: boolean
): string[] {
  const sets: string[] = [];
  for (const key of SET_KEYS) {
    if (!selected[key]) {
      continue;
    }
    const chars = excludeAmbiguous
      ? filterAmbiguous(CHAR_SETS[key])
      : CHAR_SETS[key];
    if (chars.length > 0) {
      sets.push(chars);
    }
  }
  return sets;
}

function generatePassword(sets: string[], length: number): string {
  // Ensure at least one character from each selected set.
  const chars: string[] = sets.map((set) => set[randomInt(set.length)]);
  const combined = sets.join("");
  while (chars.length < length) {
    chars.push(combined[randomInt(combined.length)]);
  }
  // Fisher-Yates shuffle so the guaranteed characters aren't predictable.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomInt(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

function entropyBits(charsetSize: number, length: number): number {
  if (charsetSize === 0) {
    return 0;
  }
  return Math.round(length * Math.log2(charsetSize));
}

interface StrengthStrings {
  weak: string;
  fair: string;
  strong: string;
  veryStrong: string;
}

function strengthBand(
  bits: number,
  strings: StrengthStrings
): { label: string; percent: number } {
  if (bits < 40) {
    return { label: strings.weak, percent: 25 };
  }
  if (bits < 60) {
    return { label: strings.fair, percent: 50 };
  }
  if (bits < 80) {
    return { label: strings.strong, percent: 75 };
  }
  return { label: strings.veryStrong, percent: 100 };
}

/** English strings. A translated content file overrides these through `ui`. */
export const PASSWORD_GENERATOR_STRINGS = {
  selectAtLeastOneCharacterType: "Select at least one character type",
  strengthLine: "Strength: {label} ({bits} bits of entropy)",
  generateNew: "Generate New",
  copied: "Copied!",
  copy: "Copy",
  length: "Length",
  setLabels: {
    lowercase: "Lowercase (a-z)",
    uppercase: "Uppercase (A-Z)",
    digits: "Digits (0-9)",
    symbols: "Symbols (!@#$...)",
  },
  excludeAmbiguousLabel: "Exclude ambiguous characters (0, O, 1, l, I)",
  weak: "Weak",
  fair: "Fair",
  strong: "Strong",
  veryStrong: "Very strong",
  privacyHeading: "Generated Locally, Never Sent",
  privacyBody:
    "Passwords are generated on your device using your browser's cryptographically secure random number generator (crypto.getRandomValues). Nothing is transmitted over the network, nothing is stored, and we cannot see the passwords you create.",
  adviceHeading: "Password Advice",
  adviceParagraph1:
    "Use a unique password for every site. When one service is breached, attackers immediately try the leaked passwords on email providers, banks, and social networks — a reused password turns one breach into many.",
  adviceParagraph2:
    "Use a password manager. Nobody can remember dozens of long random passwords, and writing them down or reusing them defeats the point. A password manager stores them encrypted, fills them in for you, and only asks you to remember one strong master passphrase.",
  adviceParagraph3:
    "Prefer length over cleverness. Substituting letters with symbols (p@ssw0rd) adds far less strength than simply making the password longer. Where a site allows it, turn on two-factor authentication as well.",
} as const;

export default function PasswordGeneratorWidget() {
  const [length, setLength] = useState(16);
  const [selected, setSelected] = useState<Record<SetKey, boolean>>({
    lowercase: true,
    uppercase: true,
    digits: true,
    symbols: true,
  });
  const [excludeAmbiguous, setExcludeAmbiguous] = useState(false);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(PASSWORD_GENERATOR_STRINGS);

  const sets = activeCharSets(selected, excludeAmbiguous);
  const charsetSize = sets.join("").length;
  const bits = entropyBits(charsetSize, length);
  const band = strengthBand(bits, {
    weak: strings.weak,
    fair: strings.fair,
    strong: strings.strong,
    veryStrong: strings.veryStrong,
  });

  function handleGenerate() {
    if (sets.length === 0) {
      return;
    }
    setPassword(generatePassword(sets, length));
    setCopied(false);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: generate once on mount only
  useEffect(() => {
    handleGenerate();
  }, []);

  function handleToggle(key: SetKey) {
    const next = { ...selected, [key]: !selected[key] };
    const anySelected = SET_KEYS.some((k) => next[k]);
    if (!anySelected) {
      return;
    }
    setSelected(next);
  }

  function handleCopy() {
    if (password.length === 0) {
      return;
    }
    writeToClipboard(password).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <>
      {/* Generated password */}
      <section className="space-y-4 rounded-lg border border-border bg-card p-6">
        <p className="break-all rounded-lg border border-border bg-background p-4 font-mono text-foreground text-lg">
          {password || strings.selectAtLeastOneCharacterType}
        </p>
        <div className="space-y-1.5">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${band.percent}%` }}
            />
          </div>
          <p className="text-muted-foreground text-sm">
            {renderSlots(strings.strengthLine, {
              label: <span className="text-foreground">{band.label}</span>,
              bits,
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
            onClick={handleGenerate}
            type="button"
          >
            {strings.generateNew}
          </button>
          <button
            className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
            disabled={password.length === 0}
            onClick={handleCopy}
            type="button"
          >
            {copied ? strings.copied : strings.copy}
          </button>
        </div>
      </section>

      {/* Options */}
      <section className="space-y-3">
        <label
          className="flex items-center justify-between gap-2 font-medium text-foreground text-sm"
          htmlFor="pw-length"
        >
          {strings.length}
          <span className="font-mono text-muted-foreground">{length}</span>
        </label>
        <input
          className="w-full accent-primary"
          id="pw-length"
          max={64}
          min={8}
          onChange={(e) => setLength(Number.parseInt(e.target.value, 10))}
          type="range"
          value={length}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          {SET_KEYS.map((key) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={key}
            >
              <input
                checked={selected[key]}
                className="accent-primary"
                onChange={() => handleToggle(key)}
                type="checkbox"
              />
              {strings.setLabels[key]}
            </label>
          ))}
        </div>
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <input
            checked={excludeAmbiguous}
            className="accent-primary"
            onChange={() => setExcludeAmbiguous(!excludeAmbiguous)}
            type="checkbox"
          />
          {strings.excludeAmbiguousLabel}
        </label>
      </section>

      {/* Privacy */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.privacyHeading}
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.privacyBody}
        </p>
      </section>

      {/* Advice */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.adviceHeading}
        </h2>
        <div className="space-y-4 text-muted-foreground text-sm leading-relaxed">
          <p>{strings.adviceParagraph1}</p>
          <p>{strings.adviceParagraph2}</p>
          <p>{strings.adviceParagraph3}</p>
        </div>
      </section>
    </>
  );
}
